import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FolderKanban, Zap, FileCheck2, Clock3, Save, Plus, Loader2, Mail } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { fmt, euros } from "@/lib/cee";
import { AnimatedNumber, FadeIn } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3 } from "lucide-react";

const MOIS_LABEL = (m) => {
  const d = new Date(`${m}-01T00:00:00`);
  return d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
};

const Admin = () => {
  const [stats, setStats] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [pendingDocs, setPendingDocs] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [emails, setEmails] = useState(null);
  const [savingEmails, setSavingEmails] = useState(false);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(apiErrorMessage(e)));
    api.get("/pricing").then(({ data }) => setPricing({ ...data, paliers: [...(data.paliers || [])].sort((a, b) => b.seuilKwhCumac - a.seuilKwhCumac) })).catch((e) => toast.error(apiErrorMessage(e)));
    api.get("/admin/documents").then(({ data }) => setPendingDocs(data)).catch((e) => toast.error(apiErrorMessage(e)));
    api.get("/admin/monthly").then(({ data }) => setMonthly(data)).catch((e) => toast.error(apiErrorMessage(e)));
    api.get("/admin/notifications-emails").then(({ data }) => setEmails(data)).catch(() => setEmails(null));
  };
  useEffect(() => { load(); }, []);

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      const { data } = await api.put("/admin/pricing", {
        prixAutre: parseFloat(pricing.prixAutre) || 0,
        paliers: pricing.paliers.map((p) => ({ seuilKwhCumac: parseFloat(p.seuilKwhCumac) || 0, prix: parseFloat(p.prix) || 0 })),
      });
      setPricing({ ...data, paliers: [...(data.paliers || [])].sort((a, b) => b.seuilKwhCumac - a.seuilKwhCumac) });
      toast.success("Tarification enregistrée — appliquée à tout le portail.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingPricing(false);
    }
  };

  const saveEmails = async () => {
    setSavingEmails(true);
    try {
      const { data } = await api.put("/admin/notifications-emails", {
        emailPartenaire: (emails.emailPartenaire || "").trim(),
        emailAdmin: (emails.emailAdmin || "").trim(),
      });
      setEmails(data);
      toast.success("Boîtes email enregistrées — les alertes partent vers ces adresses.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingEmails(false);
    }
  };

  const setDocStatus = async (docId, status) => {
    const statusComment = status === "refuse" ? window.prompt("Motif du refus (optionnel) :", "") || "" : "";
    try {
      await api.put(`/documents/${docId}`, { status, statusComment });
      toast.success(status === "valide" ? "Document validé." : "Document refusé.");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const paliers = pricing?.paliers || [];
  const pending = (pendingDocs || []).filter((d) => d.status === "en_attente");

  return (
    <div className="mx-auto max-w-[1440px] px-4 pb-24 sm:px-6">
      <div className="mb-8">
        <p className="mb-2 text-[11px] font-mono uppercase tracking-[0.24em] text-[var(--amber-text)]">Administration</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--ink)] sm:text-4xl">Pilotage du portail</h1>
      </div>

      {/* Bandeau statistiques */}
      <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { icon: FolderKanban, label: "Dossiers", value: stats?.dossiers ?? "…", tid: "admin-stat-dossiers" },
          { icon: Zap, label: "MWh cumac générés", value: stats ? fmt(stats.mwhTotal, 1) : "…", tid: "admin-stat-mwh" },
          { icon: FileCheck2, label: "Prime totale", value: stats ? euros(Math.round(stats.primeTotale)) : "…", tid: "admin-stat-prime" },
          { icon: Clock3, label: "Docs en attente", value: stats?.docsEnAttente ?? "…", tid: "admin-stat-docs" },
        ].map(({ icon: Icon, label, value, tid }, i) => (
          <motion.div key={label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07, duration: 0.5 }}
            className="card-surface p-5" data-testid={tid}>
            <Icon size={17} className="mb-3 text-[var(--accent-text)]" />
            <div className="font-display text-2xl font-extrabold text-[var(--ink)]">{typeof value === "number" ? <AnimatedNumber value={value} /> : value}</div>
            <div className="mt-1 text-[11px] uppercase tracking-wider text-[var(--mute)]">{label}</div>
          </motion.div>
        ))}
      </div>

      {/* Évolution mensuelle */}
      <FadeIn className="card-surface mb-6 p-6">
        <h2 className="font-display mb-1 flex items-center gap-2 text-lg font-bold text-[var(--ink)]">
          <BarChart3 size={17} className="text-[var(--accent-text)]" /> Primes CEE valorisées par mois
        </h2>
        <p className="mb-4 text-xs text-[var(--mute)]">Volume cumac et prime nette des dossiers créés, sur les 12 derniers mois.</p>
        {monthly && (
          monthly.some((m) => m.prime > 0 || m.dossiers > 0) ? (
            <div data-testid="admin-monthly-chart" className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={monthly.map((m) => ({ ...m, label: MOIS_LABEL(m.mois) }))}
                  margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="gPrime" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#24A0F7" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#24A0F7" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gMwh" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#06B6D4" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "var(--mute)", fontSize: 11 }} axisLine={{ stroke: "var(--line)" }} tickLine={false} />
                  <YAxis tick={{ fill: "var(--mute)", fontSize: 11 }} axisLine={false} tickLine={false} width={54} />
                  <Tooltip
                    contentStyle={{ background: "var(--panel)", border: "1px solid var(--line)", borderRadius: 12, color: "var(--ink)", fontSize: 12 }}
                    labelStyle={{ color: "var(--dim)" }}
                    formatter={(value, name) =>
                      name === "Prime (€)" ? [`${fmt(Math.round(value))} €`, name] : [fmt(value, 1), name]
                    }
                  />
                  <Area type="monotone" dataKey="prime" name="Prime (€)" stroke="#24A0F7" strokeWidth={2.5} fill="url(#gPrime)" />
                  <Area type="monotone" dataKey="mwh" name="MWh cumac" stroke="#06B6D4" strokeWidth={1.6} fill="url(#gMwh)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-[var(--line)] px-4 py-10 text-center text-xs text-[var(--mute)]">
              Aucune prime valorisée sur les 12 derniers mois — les nouveaux dossiers apparaîtront ici.
            </p>
          )
        )}
      </FadeIn>

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        {/* Tarification */}
        <FadeIn className="card-surface min-w-0 p-6">
          <h2 className="font-display mb-2 text-lg font-bold text-[var(--ink)]">Tarification du MWh cumac</h2>
          <p className="mb-5 text-xs leading-relaxed text-[var(--mute)]">
            Prix appliqué automatiquement selon le volume total du dossier : le prix de base s'applique en dessous des seuils, les paliers
            valorisent les grands volumes.
          </p>
          {pricing && (
            <div className="space-y-4">
              <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <div>
                  <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Prix de base (€/MWh)</label>
                  <input data-testid="admin-pricing-base-input" type="number" step="0.1" min="0" className="field-input font-mono"
                    value={pricing.prixAutre} onChange={(e) => setPricing((p) => ({ ...p, prixAutre: e.target.value }))} />
                </div>
                <div className="flex justify-end sm:col-span-2">
                  <button data-testid="admin-save-pricing-btn" onClick={savePricing} disabled={savingPricing} className="btn-primary">
                    {savingPricing ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
                  </button>
                </div>
              </div>
              {paliers.map((p, i) => (
                <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 rounded-xl border border-[var(--line)] bg-[var(--inset)] p-4">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">
                      Seuil palier {i + 1} (kWh cumac, &gt;)
                    </label>
                    <input data-testid={`admin-pricing-seuil-${i}`} type="number" step="1" min="0" className="field-input font-mono"
                      value={p.seuilKwhCumac}
                      onChange={(e) => setPricing((pr) => ({ ...pr, paliers: pr.paliers.map((x, j) => (j === i ? { ...x, seuilKwhCumac: e.target.value } : x)) }))} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Prix (€/MWh)</label>
                    <input data-testid={`admin-pricing-prix-${i}`} type="number" step="0.1" min="0" className="field-input font-mono"
                      value={p.prix}
                      onChange={(e) => setPricing((pr) => ({ ...pr, paliers: pr.paliers.map((x, j) => (j === i ? { ...x, prix: e.target.value } : x)) }))} />
                  </div>
                </div>
              ))}
              <button data-testid="admin-add-palier-btn"
                onClick={() => setPricing((pr) => ({ ...pr, paliers: [...pr.paliers, { seuilKwhCumac: 0, prix: pr.prixAutre }] }))}
                className="btn-ghost w-full">
                <Plus size={14} /> Ajouter un palier
              </button>
            </div>
          )}
        </FadeIn>

        {/* Documents en attente */}
        <FadeIn className="card-surface min-w-0 p-6">
          <h2 className="font-display mb-2 flex items-center gap-2 text-lg font-bold text-[var(--ink)]">
            <Clock3 size={17} className="text-[var(--amber-text)]" /> Documents à valider
            <span data-testid="admin-pending-docs-count" className="rounded-full bg-amber-500/12 px-2.5 py-0.5 font-mono text-xs text-[var(--amber-text)]">
              {pendingDocs ? pending.length : "…"}
            </span>
          </h2>
          <p className="mb-5 text-xs text-[var(--mute)]">Devis signé, facture et attestation sur l'honneur composent le dossier obligatoire.</p>
          <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
            {pendingDocs === null && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[var(--accent-text)]" /></div>}
            {pendingDocs !== null && pending.length === 0 && (
              <p className="rounded-xl border border-dashed border-[var(--line)] px-4 py-10 text-center text-xs text-[var(--mute)]">
                Aucun document en attente — tout est traité.
              </p>
            )}
            {pending.map((doc) => (
              <div key={doc.id} data-testid={`admin-doc-item-${doc.id}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--inset)] px-4 py-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[var(--ink)]">{doc.docType} <span className="text-[var(--mute)]">· {doc.dossierNom}</span></div>
                  <div className="truncate text-[11px] text-[var(--mute)]">{doc.filename} · {Math.round(doc.size / 1024)} Ko</div>
                </div>
                <a data-testid={`admin-doc-download-${doc.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`}
                  className="rounded-lg border border-[var(--line)] px-3 py-1.5 text-xs text-[var(--dim)] hover:text-[var(--accent-text)]">
                  Voir
                </a>
                <button data-testid={`admin-doc-validate-${doc.id}`} onClick={() => setDocStatus(doc.id, "valide")}
                  className="rounded-lg bg-sky-500/15 px-3 py-1.5 text-xs font-medium text-[var(--accent-text)] hover:bg-sky-500/25">
                  Valider
                </button>
                <button data-testid={`admin-doc-refuse-${doc.id}`} onClick={() => setDocStatus(doc.id, "refuse")}
                  className="rounded-lg bg-rose-500/12 px-3 py-1.5 text-xs font-medium text-[var(--rose-text)] hover:bg-rose-500/25">
                  Refuser
                </button>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>

      {/* Notifications par email */}
      <FadeIn className="card-surface mt-6 p-6">
        <h2 className="font-display mb-2 flex items-center gap-2 text-lg font-bold text-[var(--ink)]">
          <Mail size={17} className="text-[var(--accent-text)]" /> Notifications par email
        </h2>
        <p className="mb-5 text-xs leading-relaxed text-[var(--mute)]">
          Adresses des alertes automatiques : dépôt d'un document et relance 48 h côté administration, refus d'une pièce côté partenaire.
        </p>
        {emails && (
          <div className="grid items-end gap-4 sm:grid-cols-[1fr_1fr_auto]">
            <div>
              <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Email du partenaire (refus de documents)</label>
              <input
                data-testid="admin-email-partenaire-input"
                type="email" className="field-input" placeholder="partenaire@exemple.fr"
                value={emails.emailPartenaire || ""}
                onChange={(e) => setEmails((m) => ({ ...m, emailPartenaire: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Email d'administration (dépôts, relances 48 h)</label>
              <input
                data-testid="admin-email-admin-input"
                type="email" className="field-input" placeholder="admin@exemple.fr"
                value={emails.emailAdmin || ""}
                onChange={(e) => setEmails((m) => ({ ...m, emailAdmin: e.target.value }))}
              />
            </div>
            <button data-testid="admin-emails-save-btn" onClick={saveEmails} disabled={savingEmails} className="btn-primary">
              {savingEmails ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
            </button>
          </div>
        )}
      </FadeIn>

      {/* Répartitions : par statut et par fiche CEE */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {stats?.parStatut && Object.keys(stats.parStatut).length > 0 && (
          <FadeIn className="card-surface p-6">
            <h2 className="font-display mb-4 text-lg font-bold text-[var(--ink)]">Dossiers par statut</h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.parStatut).map(([statut, n]) => (
                <div key={statut} data-testid={`admin-statut-${statut}`} className="rounded-xl border border-[var(--line)] bg-[var(--inset)] px-5 py-3">
                  <span className="font-display text-xl font-bold text-[var(--ink)]">{n}</span>
                  <span className="ml-2 text-xs text-[var(--mute)]">{statut}</span>
                </div>
              ))}
            </div>
          </FadeIn>
        )}
        {stats?.parFiche && (
          <FadeIn className="card-surface p-6">
            <h2 className="font-display mb-1 text-lg font-bold text-[var(--ink)]">Dossiers par fiche CEE</h2>
            <p className="mb-4 text-xs text-[var(--mute)]">Un dossier avec plusieurs fiches compte dans chacune ; volume cumac recalculé côté portail.</p>
            <div className="space-y-3">
              {Object.entries(stats.parFiche).map(([fiche, v]) => {
                const pct = stats.dossiers ? Math.round((v.dossiers / stats.dossiers) * 100) : 0;
                return (
                  <div key={fiche} data-testid={`admin-fiche-${fiche}`}>
                    <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                      <span className="font-mono text-[var(--ink)]">{fiche}</span>
                      <span className="text-right text-xs text-[var(--mute)]">
                        <b className="text-[var(--ink)]">{v.dossiers}</b> dossier{v.dossiers > 1 ? "s" : ""} · {fmt(v.kwh / 1000, 1)} MWh cumac
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-[var(--inset)]">
                      <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </FadeIn>
        )}
      </div>
    </div>
  );
};

export default Admin;
